import { Helmet } from '../documentHead';
import { useTranslation } from 'react-i18next';
import { useLocation, stripLang, getLangFromPath } from '../router';
import config from '../config';
import { buildBreadcrumbSchema } from '../utils/schema';

const SEOHead = ({
    pageKey,
    titleOverride = null,
    descriptionOverride = null,
    imageOverride = null,
    schemaMarkup = null,
    breadcrumb = null,
    noindex = false
}) => {
    const { t, i18n } = useTranslation('seo');
    const location = useLocation();

    const baseUrl = (process.env.REACT_APP_SITE_URL || config.siteUrl || window.location.origin).replace(/\/$/, '');

    // Build every URL from the language-prefixed scheme. The logical path (no
    // language segment) is the same across languages; each variant just gets a
    // different prefix. The current language comes from the URL (source of
    // truth), falling back to i18n while a redirect is in flight.
    const logicalPath = stripLang(location.pathname);
    const currentLang = getLangFromPath(location.pathname) || i18n.language || config.defaultLanguage;
    const localizedUrl = (lang) => `${baseUrl}/${lang}${logicalPath === '/' ? '' : logicalPath}`;

    const canonicalUrl = localizedUrl(currentLang);

    // Get translated title and description
    const title = titleOverride || t(`${pageKey}.title`);
    const description = descriptionOverride || t(`${pageKey}.description`);

    // Collect every JSON-LD node to emit: caller-supplied schema(s) plus an
    // optional breadcrumb built from { name, path } crumbs. Each becomes its own
    // <script> tag (the custom Helmet appends one element per script child).
    const schemas = [];
    if (schemaMarkup) {
        schemas.push(...(Array.isArray(schemaMarkup) ? schemaMarkup : [schemaMarkup]));
    }
    if (breadcrumb && breadcrumb.length) {
        // Breadcrumb item URLs must point at the real (language-prefixed) pages.
        schemas.push(buildBreadcrumbSchema(breadcrumb, `${baseUrl}/${currentLang}`));
    }

    const ogImage = imageOverride || `${baseUrl}${config.ogImage}`;
    const ogImageAlt = t('ogImageAlt');
    const ogLocales = {
        en: 'en_US',
        de: 'de_DE',
        es: 'es_ES',
        fr: 'fr_FR',
        it: 'it_IT',
        pt: 'pt_PT'
    };

    return (
        <Helmet>
            {/* Primary Meta Tags */}
            <title>{title}</title>
            <meta name="description" content={description} />
            <html lang={currentLang} />

            {/* A 404 (or any noindex page) must not be indexed, and should not
                advertise a canonical or hreflang alternates. */}
            {noindex && <meta name="robots" content="noindex, follow" />}
            {!noindex && <link rel="canonical" href={canonicalUrl} />}

            {/* hreflang alternates — tell search engines about every language
                variant of this page so the right one is served per locale. */}
            {!noindex && config.languages.map((lang) => (
                <link key={lang} rel="alternate" hrefLang={lang} href={localizedUrl(lang)} />
            ))}
            {!noindex && <link rel="alternate" hrefLang="x-default" href={localizedUrl(config.defaultLanguage)} />}

            {/* Open Graph / Facebook */}
            <meta property="og:type" content="website" />
            <meta property="og:site_name" content={config.propertyName} />
            <meta property="og:url" content={canonicalUrl} />
            <meta property="og:title" content={title} />
            <meta property="og:description" content={description} />
            <meta property="og:image" content={ogImage} />
            {/* Dimensions only for the dedicated 1200×630 share image; per-page
                override images (e.g. an apartment hero) have unknown sizes. */}
            {!imageOverride && <meta property="og:image:width" content="1200" />}
            {!imageOverride && <meta property="og:image:height" content="630" />}
            <meta property="og:image:alt" content={ogImageAlt} />
            <meta property="og:locale" content={ogLocales[currentLang] || currentLang} />

            {/* Twitter */}
            <meta property="twitter:card" content="summary_large_image" />
            <meta property="twitter:url" content={canonicalUrl} />
            <meta property="twitter:title" content={title} />
            <meta property="twitter:description" content={description} />
            <meta property="twitter:image" content={ogImage} />
            <meta property="twitter:image:alt" content={ogImageAlt} />

            {/* Schema.org Markup — one script per JSON-LD node */}
            {schemas.map((schema, index) => (
                <script key={index} type="application/ld+json">
                    {JSON.stringify(schema)}
                </script>
            ))}
        </Helmet>
    );
};

export default SEOHead;
